import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import TelLink from "@/components/TelLink";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    ArrowRight,
    Baby,
    Calendar,
    CheckCircle,
    Clock,
    CreditCard,
    DollarSign,
    Info,
    MapPin,
    Phone,
    Shield,
    Users,
} from "lucide-react";

type PaymentMethod = {
    title: string;
    tableLabel: string;
    subtitle: string;
    icon: React.ComponentType<{ className?: string }>;
    summary: string;
    approvalTime: string;
    fees: string;
    bestFor: string;
    bullets: string[];
    paragraphs: string[];
    callout: string;
    accent: string;
};

type FAQItem = {
    q: string;
    a: string;
};

type CostRow = {
    treatment: string;
    priceGuide: string;
    commonOption: string;
    note: string;
};

const heroBadges = [
    "Transparent pricing",
    "Same-day help",
    "CDBS accepted",
    "Flexible support",
];

const paymentMethods: PaymentMethod[] = [
    {
        title: "Pay on the Day",
        tableLabel: "Card payment at reception",
        subtitle: "Simple, fast and available for every visit",
        icon: CreditCard,
        summary:
            "Pay at the end of your appointment using EFTPOS, Visa, Mastercard or American Express, then leave with your receipt and next steps clearly explained.",
        approvalTime: "Immediate",
        fees: "No clinic fee",
        bestFor: "Routine appointments, emergency care and smaller treatments",
        bullets: [
            "Pay securely at reception after your appointment",
            "Ask for a printed or emailed receipt before you leave",
            "Keep the process simple when you do not need a longer plan",
        ],
        paragraphs: [
            "This is the most direct option for patients who want to keep things simple and settle the account in one step.",
            "It works well for check-ups, cleans, fillings and urgent visits because the treatment is completed, the fee is clear, and payment can be handled immediately at reception.",
        ],
        callout:
            "If you are comparing dental payment plans Broadmeadows patients can use, this option is the easiest for small or single-visit treatment.",
        accent: "bg-blue-50",
    },
    {
        title: "Private Health Insurance with HICAPS",
        tableLabel: "Instant health fund claiming",
        subtitle: "Claim rebates on the spot at the desk",
        icon: Shield,
        summary:
            "Y3 Smiles Dental accepts every Australian private health fund — including Bupa, Medibank, HCF, HBF, NIB, ahm, Australian Unity, GMHBA, Defence Health, CBHS, Frank and every other registered fund. We process your claim instantly at reception using HICAPS, so you only pay the gap (if any) on the day. No paper forms. No waiting weeks for a reimbursement.",
        approvalTime: "Immediate for most funds",
        fees: "Depends on your cover and annual limits",
        bestFor: "Patients with extras cover who want instant rebates",
        bullets: [
            "Bring your physical or digital health fund card to every appointment",
            "We swipe your card at reception — your rebate is calculated and applied immediately on the spot",
            "You only pay any remaining gap on the day",
            "Works with Bupa, Medibank, HCF, HBF, NIB, ahm, Australian Unity, GMHBA, Defence Health, CBHS, Frank, Latrobe, Teachers Health, Police Health, Navy Health and every other Australian fund",
            "Itemised receipts and claim records emailed to you automatically",
        ],
        paragraphs: [
            "Private health insurance can make routine and restorative care more predictable because part of the bill may be claimed straight away through HICAPS.",
            "We work with all major Australian health funds and we are transparent about your gap payment, so you know what is covered before treatment begins.",
        ],
        callout:
            "Not a 'preferred provider' — and why that often saves you money. Some funds (especially Bupa, Medibank and HCF) promise slightly higher rebates if you visit one of their preferred-provider clinics. The trade-off: preferred providers typically charge higher base fees, run shorter appointments, or limit the materials they offer in order to recover that rebate margin. Y3 Smiles Dental sets prices independently and prioritises longer appointments, premium materials, and the treatment plan that is genuinely right for you. In many cases your total out-of-pocket cost is the same as — or even less than — a preferred-provider clinic, while you receive a higher standard of care.",
        accent: "bg-green-50",
    },
    {
        title: "Child Dental Benefits Schedule (CDBS)",
        tableLabel: "Medicare support for eligible children",
        subtitle: "Bulk-billed dental care for eligible kids 0-17",
        icon: Baby,
        summary:
            "Eligible children can receive approved basic dental services under the Medicare Child Dental Benefits Schedule, which may cover up to $1,158 over two calendar years.",
        approvalTime: "Eligibility checked before treatment",
        fees: "Covered services may be bulk-billed",
        bestFor: "Families wanting affordable check-ups and essential children's dental care",
        bullets: [
            "We can check eligibility before your appointment",
            "Covered services may include check-ups, X-rays, cleaning, fillings and extractions",
            "Families from nearby suburbs can ask us if the CDBS cap still has room left",
        ],
        paragraphs: [
            "CDBS is one of the most useful options for Broadmeadows families because it helps eligible children access essential dental care without the same out-of-pocket pressure as private treatment.",
            "If your child qualifies, we can usually confirm the benefit before treatment and explain exactly which services are covered under the remaining Medicare cap.",
        ],
        callout:
            "If you have been searching for a CDBS dentist Broadmeadows families can trust, this is the option we check first for eligible children.",
        accent: "bg-pink-50",
    },
    {
        title: "Afterpay",
        tableLabel: "Split the cost into four instalments",
        subtitle: "A simple way to spread the bill over time",
        icon: DollarSign,
        summary:
            "Afterpay lets you divide the cost into four instalments paid every two weeks, which can help with smaller or mid-sized treatments when you want to spread the cash flow.",
        approvalTime: "Usually immediate through Afterpay",
        fees: "No interest when paid on time; Afterpay terms apply",
        bestFor: "Patients who want a short repayment window",
        bullets: [
            "Set up an Afterpay account and link your card",
            "Choose Afterpay at reception when it suits your budget",
            "The first instalment is usually taken on the day of treatment",
        ],
        paragraphs: [
            "Afterpay is a popular choice for patients who want a shorter repayment plan without taking on a longer finance arrangement.",
            "It can work well for emergency visits, small restorative treatment and preventive care when you want a quick approval path and predictable instalments.",
        ],
        callout:
            "This is one of the most common answers to the search phrase afterpay dentist, especially for patients who want a quick approval process.",
        accent: "bg-purple-50",
    },
    {
        title: "Humm payment plans",
        tableLabel: "Longer-term repayment plans",
        subtitle: "Flexible repayments for larger treatment",
        icon: Clock,
        summary:
            "Humm can help patients spread the cost of larger or staged dental treatment across a longer period with a repayment schedule that better matches the overall treatment plan.",
        approvalTime: "Fast online assessment",
        fees: "Depends on the product and repayment schedule",
        bestFor: "Crowns, bridges, implants and staged rehabilitation",
        bullets: [
            "You apply directly with Humm using their own process",
            "A longer treatment plan can be spread across manageable repayments",
            "The repayment schedule is set by Humm, not the clinic",
        ],
        paragraphs: [
            "Humm is often useful for bigger treatment plans because it can help patients manage the timing of payments without delaying clinically important care.",
            "If your treatment plan includes multiple stages, our team can discuss whether Humm looks practical for your budget before you decide to apply.",
        ],
        callout:
            "Many patients searching for humm dental options want a practical way to start treatment without waiting months to save the full amount.",
        accent: "bg-orange-50",
    },
    {
        title: "Early Release of Superannuation",
        tableLabel: "ATO compassionate-release pathway",
        subtitle: "Information only for eligible patients",
        icon: Users,
        summary:
            "In some situations, the ATO may allow early access to superannuation on compassionate grounds for essential dental care, but the approval process is handled by the ATO and your super fund.",
        approvalTime: "Depends on ATO assessment",
        fees: "Set by ATO and super fund rules",
        bestFor: "Clinically necessary treatment when other options are not suitable",
        bullets: [
            "The ATO decides whether compassionate release is approved",
            "Supporting clinical documentation may be provided if required",
            "The clinic does not make the approval decision or provide financial advice",
        ],
        paragraphs: [
            "Some patients explore early release of super when the cost of treatment is significant and the work is clinically important.",
            "We can provide clinical documents where appropriate, but you should always get independent financial advice before relying on superannuation for treatment funding.",
        ],
        callout:
            "If you are asking about early super for dental, the safest next step is to confirm eligibility with the ATO and your financial adviser.",
        accent: "bg-indigo-50",
    },
];

const comparisonRows = [
    {
        label: "Pay on the Day",
        approvalTime: "Immediate",
        fees: "No clinic fee",
        bestFor: "Routine care and one-off treatment",
    },
    {
        label: "Private health with HICAPS",
        approvalTime: "Immediate for most funds",
        fees: "Depends on cover and annual limits",
        bestFor: "Patients who want on-the-spot rebates",
    },
    {
        label: "CDBS",
        approvalTime: "Eligibility checked first",
        fees: "Covered items may be bulk-billed",
        bestFor: "Eligible children aged 0-17",
    },
    {
        label: "Afterpay",
        approvalTime: "Usually immediate",
        fees: "No interest when paid on time",
        bestFor: "Short repayment windows",
    },
    {
        label: "Humm",
        approvalTime: "Fast online assessment",
        fees: "Depends on the product",
        bestFor: "Larger staged treatment",
    },
    {
        label: "Superannuation release",
        approvalTime: "ATO decision required",
        fees: "Rules set by the ATO and your fund",
        bestFor: "Clinically necessary high-cost treatment",
    },
];

const costRows: CostRow[] = [
    {
        treatment: "New patient check-up and clean",
        priceGuide: "$180-$300",
        commonOption: "Pay on the day or private health",
        note: "Often the simplest starting point for patients who want a routine assessment and clear quote.",
    },
    {
        treatment: "X-rays and diagnostic imaging",
        priceGuide: "$40-$200",
        commonOption: "Health fund or pay on the day",
        note: "Usually included in the broader assessment rather than priced as a stand-alone visit.",
    },
    {
        treatment: "Small filling",
        priceGuide: "$180-$350",
        commonOption: "Private health or Afterpay",
        note: "Useful when you want to fix early decay before it becomes a larger problem.",
    },
    {
        treatment: "Emergency consultation",
        priceGuide: "$120-$250",
        commonOption: "Pay on the day or private health",
        note: "A fast appointment can often stop pain from getting worse and reduce later treatment costs.",
    },
    {
        treatment: "Root canal treatment",
        priceGuide: "$900-$2,500+",
        commonOption: "Humm or superannuation release",
        note: "The total depends on the tooth, the number of canals and whether a crown is also required.",
    },
    {
        treatment: "Crown",
        priceGuide: "$1,400-$2,200",
        commonOption: "Humm or private health",
        note: "Often recommended when a tooth needs strength after cracking, large decay or root canal treatment.",
    },
    {
        treatment: "Wisdom tooth removal",
        priceGuide: "$350-$1,000+",
        commonOption: "Afterpay or Humm",
        note: "Price varies depending on position, complexity and whether a surgical approach is required.",
    },
    {
        treatment: "Single dental implant",
        priceGuide: "$4,000-$6,000+",
        commonOption: "Humm or superannuation release",
        note: "Implant planning often includes imaging, surgery, healing time and the final crown.",
    },
    {
        treatment: "Teeth whitening",
        priceGuide: "$350-$650",
        commonOption: "Afterpay or pay on the day",
        note: "Popular for patients who want a cosmetic refresh without multiple visits.",
    },
    {
        treatment: "Children's treatment under CDBS",
        priceGuide: "$0 for eligible covered services",
        commonOption: "CDBS",
        note: "Eligibility and remaining cap are checked before treatment so families know what is covered.",
    },
];

const cdbsSuburbs = [
    {
        name: "Broadmeadows",
        href: "/locations/broadmeadows/",
        note: "Closest option for local families who want convenient children's dental care.",
    },
    {
        name: "Glenroy",
        href: "/locations/glenroy/",
        note: "Handy for families along Pascoe Vale Road and nearby residential streets.",
    },
    {
        name: "Meadow Heights",
        href: "/locations/meadow-heights/",
        note: "A short drive for parents who want a calm, family-friendly clinic.",
    },
    {
        name: "Roxburgh Park",
        href: "/locations/roxburgh-park/",
        note: "Useful for households looking for a nearby CDBS provider with flexible appointment times.",
    },
    {
        name: "Campbellfield",
        href: "/locations/campbellfield/",
        note: "Close enough for quick after-school appointments and check-ups.",
    },
    {
        name: "Greenvale",
        href: "/locations/greenvale/",
        note: "A straightforward option when families prefer a Broadmeadows clinic with easy parking.",
    },
    {
        name: "Gladstone Park",
        href: "/locations/gladstone-park/",
        note: "Good for parents balancing school, work and children's preventive care.",
    },
    {
        name: "Hadfield",
        href: "/locations/hadfield/",
        note: "A practical choice for local families who want same-day availability if possible.",
    },
    {
        name: "Craigieburn",
        href: "/locations/craigieburn/",
        note: "Often searched by families wanting bulk-billed children's dental options near Broadmeadows.",
    },
    {
        name: "Mickleham",
        href: "/locations/mickleham/",
        note: "Helpful for growing families who want an accessible clinic with flexible payment support.",
    },
];

const faqItems: FAQItem[] = [
    {
        q: "Do you offer dental payment plans in Broadmeadows?",
        a: "Yes. We offer several ways to manage treatment costs, including pay on the day, HICAPS claiming for private health funds, Afterpay, Humm and other pathways where eligibility or approval is available.",
    },
    {
        q: "Can I use Afterpay at the dentist?",
        a: "Yes. Afterpay can be used for eligible treatment when your account is set up and approved through Afterpay's own process. It is a practical short-term option for patients who want to split the bill into four instalments.",
    },
    {
        q: "Is Humm a good option for larger dental treatment?",
        a: "Humm is often a practical option for larger or staged dental work because it can spread repayments over a longer period. We can help you understand whether the treatment plan looks suitable before you apply.",
    },
    {
        q: "Do you accept private health insurance and HICAPS?",
        a: "Yes. Y3 Smiles Dental accepts every Australian private health fund — including Bupa, Medibank, HCF, HBF, NIB, ahm, Australian Unity, GMHBA, Defence Health, CBHS, Frank and all others. We use HICAPS to process your claim instantly at reception. You swipe your card on the day, the rebate is calculated and applied immediately, and you only pay any remaining gap. No paper forms, no waiting weeks for reimbursement.",
    },
    {
        q: "Are you a Bupa, Medibank or HCF preferred provider in Broadmeadows?",
        a: "Y3 Smiles Dental is intentionally not a preferred provider for any specific health fund. Preferred-provider arrangements give patients a slightly higher rebate but require clinics to discount fees, which often means shorter appointments, cheaper materials, or treatment limits set by the fund rather than the dentist. We chose to set our own fees so we can offer longer appointments, premium materials and the treatment plan that is genuinely right for you. We accept every Australian fund via HICAPS — and in many cases your total out-of-pocket cost is the same as or less than a preferred-provider clinic, with better quality care.",
    },
    {
        q: "What is the Child Dental Benefits Schedule (CDBS)?",
        a: "The CDBS is a Medicare program for eligible children aged 0-17. It can help cover basic dental care such as check-ups, X-rays, cleaning, fillings and some extractions, up to the current two-year cap.",
    },
    {
        q: "Can my child get bulk-billed dental care through CDBS?",
        a: "If your child is eligible for CDBS and the treatment is covered by the scheme, we may be able to bulk-bill those services so there is no out-of-pocket cost for the covered items. We check eligibility before treatment.",
    },
    {
        q: "Can I use my superannuation to pay for dental treatment?",
        a: "In some cases, the ATO may allow early release of superannuation on compassionate grounds for essential dental care. Approval is not made by the clinic, so you should confirm eligibility directly with the ATO and a financial adviser.",
    },
    {
        q: "How much does a dental check-up cost in Broadmeadows?",
        a: "A routine check-up and clean at a private clinic can vary depending on the treatment needed, whether X-rays are required and whether your health fund covers part of the visit. We explain the costs before treatment begins.",
    },
    {
        q: "Do you offer payment plans for implants and crowns?",
        a: "Yes. Larger restorative treatments such as implants and crowns are often the cases where patients use Humm, Afterpay, health fund rebates or other staged payment support. We can discuss the options after your consultation.",
    },
    {
        q: "Are Health Care Card holders eligible for free dental in Victoria?",
        a: "Eligibility depends on the Victorian public dental rules and the service pathway you qualify for at the time. If you think you may be eligible, we can help explain when a public pathway might be suitable and what happens next.",
    },
    {
        q: "How do I know which payment option is best for me?",
        a: "The best option depends on the size of the treatment, whether your child is CDBS-eligible, if you have private health insurance, and how quickly you want to start care. Our team can walk you through the choice and help you compare the likely out-of-pocket cost.",
    },
];

const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
            "@type": "Answer",
            text: item.a,
        },
    })),
};

const resourceLinks = [
    {
        title: "Full pricing guide",
        href: "/patient-resources/pricing/",
        description:
            "See the broader price guide if you want a treatment-by-treatment overview before booking.",
    },
    {
        title: "Dental payment options in Australia",
        href: "/news-and-articles/dental-payment-options-australia",
        description:
            "Read the companion article that explains how payment plans and health fund support work in Australia.",
    },
    {
        title: "How much dental treatment costs in Australia",
        href: "/news-and-articles/dental-treatment-costs-australia",
        description:
            "Use this guide if you want a practical overview of typical treatment costs and why prices vary.",
    },
    {
        title: "Does my child qualify for free dental through CDBS?",
        href: "/news-and-articles/does-my-child-qualify-free-dental-cdbs-broadmeadows",
        description:
            "Check the article that explains CDBS eligibility, age limits and how parents can confirm the benefit.",
    },
    {
        title: "Dental implants",
        href: "/treatments/dental-implants",
        description:
            "Learn more about implant treatment if you are comparing larger restorative options.",
    },
    {
        title: "Children's dentistry",
        href: "/treatments/childrens-dentistry",
        description:
            "See how we look after younger patients, including preventive care and CDBS support.",
    },
];

const paymentSteps = [
    {
        title: "1. We review the treatment",
        text: "Your dentist explains the problem, the recommended treatment and any alternatives so you can understand the cost before you commit.",
    },
    {
        title: "2. You compare payment methods",
        text: "You can choose the simplest option for a smaller visit or a longer repayment path for bigger work, depending on your budget and timing.",
    },
    {
        title: "3. We confirm support or eligibility",
        text: "If a health fund claim, CDBS benefit or superannuation-related document is relevant, we help you work through the paperwork.",
    },
    {
        title: "4. You start treatment with clarity",
        text: "You leave the consultation knowing what will happen next, what the likely cost is and which option will keep the plan manageable.",
    },
];

const trustPoints = [
    {
        title: "Upfront quotes",
        text: "We explain the likely cost before treatment starts, then confirm the details again if the plan changes.",
    },
    {
        title: "Supportive admin",
        text: "Our team helps with claims, receipts and documentation so the admin side feels less stressful.",
    },
    {
        title: "Flexible timing",
        text: "We can help you decide whether to begin immediately, stage treatment or use a payment path that suits the situation.",
    },
];

const PaymentOptionsPage = () => {
    useScrollAnimation();

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <SEO
                title="Dental Payment Options in Broadmeadows | Plans, HICAPS, Medicare CDBS | Y3 Smiles Dental"
                description="Flexible dental payment options in Broadmeadows with HICAPS, CDBS, Afterpay, Humm and superannuation support."
                canonicalUrl="/patients/payment-options/"
                image="/og-reception.jpg"
            >
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </SEO>
            <Header />
            <main className="flex-grow">
                <section className="relative overflow-hidden bg-gradient-to-br from-neutral-50 via-primary/5 to-secondary/5 py-24 lg:py-32">
                    <div className="absolute inset-0 opacity-40">
                        <div className="absolute left-10 top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl"></div>
                        <div className="absolute right-12 top-24 h-56 w-56 rounded-full bg-secondary/10 blur-3xl"></div>
                        <div className="absolute bottom-8 left-1/3 h-48 w-48 rounded-full bg-primary/10 blur-3xl"></div>
                    </div>
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-5xl mx-auto text-center">
                            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/90 px-4 py-2 text-sm font-semibold text-primary shadow-soft mb-8">
                                <MapPin className="h-4 w-4" />
                                Broadmeadows dental payment support
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold text-neutral-800 leading-tight mb-6">
                                Dental Payment Options in Broadmeadows
                            </h1>
                            <p className="text-lg md:text-xl text-neutral-800 leading-relaxed max-w-4xl mx-auto mb-6">
                                If you are comparing dental payment plans Broadmeadows patients actually use, this guide shows the main ways to pay for treatment at Y3 Smiles Dental.
                                We explain pay on the day, HICAPS claims, Afterpay, Humm, CDBS and early release of superannuation so you can choose a path that fits your budget.
                            </p>
                            <p className="text-lg text-neutral-800 leading-relaxed max-w-4xl mx-auto mb-10">
                                The aim is simple: help you get treatment sooner, understand the likely fee before you start and avoid last-minute surprises.
                                Whether you need a no gap dentist Broadmeadows families can use for children, a bulk billed dentist for an eligible child, or a longer plan for an implant or crown, our team will talk through the practical steps with you.
                            </p>
                            <div className="flex flex-wrap justify-center gap-3 mb-10">
                                {heroBadges.map((badge) => (
                                    <div key={badge} className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 shadow-soft">
                                        <CheckCircle className="h-4 w-4 text-primary" />
                                        <span className="text-sm font-medium text-neutral-700">{badge}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <TelLink clickSource="payment_options_hero">
                                        <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        Call 03 9022 4442
                                    </TelLink>
                                </Button>
                                <Button variant="outline" size="xl" asChild>
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <Calendar className="w-5 h-5 mr-2" />
                                        Book Online
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="max-w-3xl mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                    Quick comparison of payment options
                                </h2>
                                <p className="text-lg text-neutral-800 leading-relaxed">
                                    Use this overview to compare how quickly each option can be approved, what fees may apply and which treatment type it is usually best suited to.
                                </p>
                            </div>
                            <div className="overflow-hidden rounded-3xl border border-neutral-200 shadow-soft">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-neutral-200 bg-white">
                                        <thead className="bg-neutral-50">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Method</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Approval time</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Fees</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Best for</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-neutral-200">
                                            {comparisonRows.map((row) => (
                                                <tr key={row.label} className="align-top">
                                                    <td className="px-6 py-5 text-sm font-semibold text-neutral-800">{row.label}</td>
                                                    <td className="px-6 py-5 text-sm text-neutral-700">{row.approvalTime}</td>
                                                    <td className="px-6 py-5 text-sm text-neutral-700">{row.fees}</td>
                                                    <td className="px-6 py-5 text-sm text-neutral-700">{row.bestFor}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="max-w-3xl mb-12">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                    Payment options explained
                                </h2>
                                <p className="text-lg text-neutral-800 leading-relaxed">
                                    The cards below explain the main ways our patients pay for treatment.
                                    Each option has different approval steps, so we keep the wording simple and practical.
                                </p>
                            </div>
                            <div className="space-y-8">
                                {paymentMethods.map((method, index) => {
                                    const IconComponent = method.icon;
                                    const isEven = index % 2 === 0;

                                    return (
                                        <div key={method.title} className="fade-in-section" style={{ transitionDelay: `${index * 80}ms` }}>
                                            <Card className="overflow-hidden border-0 shadow-soft hover:shadow-medium transition-all">
                                                <CardContent className="p-0">
                                                    <div className={`grid grid-cols-1 ${index % 3 === 0 ? "lg:grid-cols-2" : "lg:grid-cols-12"} gap-0`}>
                                                        <div className={`${index % 3 === 0 ? "lg:col-span-6" : "lg:col-span-5"} ${isEven ? "lg:order-1" : "lg:order-2"} ${method.accent} p-8 lg:p-10`}>
                                                            <div className="w-16 h-16 rounded-2xl bg-white shadow-soft flex items-center justify-center mb-6">
                                                                <IconComponent className="w-8 h-8 text-primary" />
                                                            </div>
                                                            <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
                                                                {method.subtitle}
                                                            </p>
                                                            <h3 className="text-3xl font-bold text-neutral-800 mb-4">
                                                                {method.title}
                                                            </h3>
                                                            <p className="text-neutral-800 leading-relaxed mb-5">
                                                                {method.summary}
                                                            </p>
                                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                                                                <div className="rounded-2xl bg-white/80 p-4 shadow-sm">
                                                                    <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Approval time</p>
                                                                    <p className="font-semibold text-neutral-800">{method.approvalTime}</p>
                                                                </div>
                                                                <div className="rounded-2xl bg-white/80 p-4 shadow-sm">
                                                                    <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Fees</p>
                                                                    <p className="font-semibold text-neutral-800">{method.fees}</p>
                                                                </div>
                                                                <div className="rounded-2xl bg-white/80 p-4 shadow-sm">
                                                                    <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Best for</p>
                                                                    <p className="font-semibold text-neutral-800">{method.bestFor}</p>
                                                                </div>
                                                            </div>
                                                            <div className="bg-white/80 rounded-2xl p-4 border border-white/70">
                                                                <p className="text-sm font-semibold text-neutral-800 mb-2">Why patients choose it</p>
                                                                <p className="text-sm text-neutral-700 leading-relaxed">{method.callout}</p>
                                                            </div>
                                                        </div>

                                                        <div className={`${index % 3 === 0 ? "lg:col-span-6" : "lg:col-span-7"} ${isEven ? "lg:order-2" : "lg:order-1"} p-8 lg:p-10 bg-white`}>
                                                            <div className="space-y-5">
                                                                {method.paragraphs.map((paragraph) => (
                                                                    <p key={paragraph} className="text-neutral-800 leading-relaxed text-lg">
                                                                        {paragraph}
                                                                    </p>
                                                                ))}
                                                            </div>

                                                            <div className="mt-8 grid gap-3">
                                                                {method.bullets.map((bullet) => (
                                                                    <div key={bullet} className="flex items-start gap-3 rounded-2xl bg-neutral-50 p-4">
                                                                        <CheckCircle className="h-5 w-5 flex-shrink-0 text-primary mt-0.5" />
                                                                        <p className="text-neutral-800 leading-relaxed">{bullet}</p>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            <div className="mt-8 rounded-2xl border border-secondary/20 bg-secondary/10 p-4 flex items-start gap-3">
                                                                <Info className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                                                                <p className="text-sm text-neutral-800 leading-relaxed">{method.callout}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="max-w-3xl mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                    Typical dental costs and the payment path that often suits them
                                </h2>
                                <p className="text-lg text-neutral-800 leading-relaxed">
                                    Transparent pricing matters because the right payment method depends on the size of the treatment, how quickly you want to start and whether any insurance or Medicare benefit applies.
                                    The table below gives a practical guide, not a fixed quote.
                                </p>
                            </div>

                            <div className="overflow-hidden rounded-3xl border border-neutral-200 shadow-soft">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-neutral-200 bg-white">
                                        <thead className="bg-neutral-50">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Treatment</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Typical guide</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Common option</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-neutral-700">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-neutral-200">
                                            {costRows.map((row) => (
                                                <tr key={row.treatment} className="align-top">
                                                    <td className="px-6 py-5 text-sm font-semibold text-neutral-800">{row.treatment}</td>
                                                    <td className="px-6 py-5 text-sm text-neutral-700">{row.priceGuide}</td>
                                                    <td className="px-6 py-5 text-sm text-neutral-700">{row.commonOption}</td>
                                                    <td className="px-6 py-5 text-sm text-neutral-700">{row.note}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                                {trustPoints.map((point) => (
                                    <Card key={point.title} className="border-0 shadow-soft">
                                        <CardContent className="p-6">
                                            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                                                <CheckCircle className="w-6 h-6 text-primary" />
                                            </div>
                                            <h3 className="text-xl font-bold text-neutral-800 mb-3">{point.title}</h3>
                                            <p className="text-neutral-800 leading-relaxed">{point.text}</p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="max-w-3xl mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                    How to choose the right payment option
                                </h2>
                                <p className="text-lg text-neutral-800 leading-relaxed">
                                    Patients often ask whether they should pay on the day, claim through their health fund, use a plan or look for a Medicare or public pathway.
                                    The answer depends on the treatment, the urgency and how much flexibility you need.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {paymentSteps.map((step, index) => (
                                    <Card key={step.title} className="border-0 shadow-soft hover:shadow-medium transition-all">
                                        <CardContent className="p-6 lg:p-8">
                                            <div className="flex items-start gap-4">
                                                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-xl font-bold text-primary">{index + 1}</span>
                                                </div>
                                                <div>
                                                    <h3 className="text-2xl font-bold text-neutral-800 mb-3">{step.title}</h3>
                                                    <p className="text-neutral-800 leading-relaxed">{step.text}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="max-w-3xl mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                    CDBS suburb support for nearby families
                                </h2>
                                <p className="text-lg text-neutral-800 leading-relaxed">
                                    If your child is eligible for CDBS, we can help you work out whether there is still room in the benefit cap and whether the planned care is covered.
                                    Families from nearby suburbs often want a clinic that can check eligibility quickly, explain the treatment and keep the process simple.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                {cdbsSuburbs.map((suburb) => (
                                    <Link
                                        key={suburb.name}
                                        to={suburb.href}
                                        className="group rounded-2xl border border-neutral-200 bg-neutral-50 p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-medium"
                                    >
                                        <div className="flex items-start gap-3 mb-3">
                                            <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                            <h3 className="text-lg font-bold text-neutral-800 group-hover:text-primary transition-colors">
                                                {suburb.name}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-neutral-700 leading-relaxed">{suburb.note}</p>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="max-w-3xl mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                    FAQs about payment, pricing and support
                                </h2>
                                <p className="text-lg text-neutral-800 leading-relaxed">
                                    These answers match the structured data on the page so search engines and patients see the same information.
                                </p>
                            </div>

                            <div className="space-y-5">
                                {faqItems.map((item) => (
                                    <Card key={item.q} className="border-0 shadow-soft">
                                        <CardContent className="p-6 lg:p-8">
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <Info className="w-6 h-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h3 className="text-xl md:text-2xl font-bold text-neutral-800 mb-3">{item.q}</h3>
                                                    <p className="text-neutral-800 leading-relaxed">{item.a}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="max-w-3xl mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                    Related resources and deeper reading
                                </h2>
                                <p className="text-lg text-neutral-800 leading-relaxed">
                                    If you want more detail, these pages and articles expand on pricing, children's dental care and treatment costs in Australia.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {resourceLinks.map((resource) =>
                                    resource.href === "/news-and-articles/dental-payment-options-australia" ? (
                                        <Link
                                            key={resource.title}
                                            to="/news-and-articles/dental-payment-options-australia"
                                            className="group block rounded-3xl border border-neutral-200 bg-white p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-medium"
                                        >
                                            <div className="flex items-center justify-between gap-4 mb-4">
                                                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                                                    <ArrowRight className="h-6 w-6 text-primary group-hover:translate-x-1 transition-transform" />
                                                </div>
                                                <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Internal resource</span>
                                            </div>
                                            <h3 className="text-xl font-bold text-neutral-800 mb-3 group-hover:text-primary transition-colors">
                                                {resource.title}
                                            </h3>
                                            <p className="text-neutral-700 leading-relaxed">{resource.description}</p>
                                        </Link>
                                    ) : (
                                        <Link
                                            key={resource.title}
                                            to={resource.href}
                                            className="group block rounded-3xl border border-neutral-200 bg-white p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-medium"
                                        >
                                            <div className="flex items-center justify-between gap-4 mb-4">
                                                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                                                    <ArrowRight className="h-6 w-6 text-primary group-hover:translate-x-1 transition-transform" />
                                                </div>
                                                <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Internal resource</span>
                                            </div>
                                            <h3 className="text-xl font-bold text-neutral-800 mb-3 group-hover:text-primary transition-colors">
                                                {resource.title}
                                            </h3>
                                            <p className="text-neutral-700 leading-relaxed">{resource.description}</p>
                                        </Link>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-gradient-to-br from-primary/10 via-white to-secondary/10">
                    <div className="container mx-auto px-6">
                        <div className="max-w-5xl mx-auto text-center">
                            <Phone className="w-16 h-16 text-primary mx-auto mb-6" />
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                                Talk to us about the payment option that suits your treatment
                            </h2>
                            <p className="text-lg text-neutral-800 leading-relaxed max-w-3xl mx-auto mb-8">
                                If you are comparing afterpay dentist options, asking about bulk billed dentist eligibility, or trying to work out whether a larger treatment should be split into stages, call our Broadmeadows team and we will explain the next step clearly.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <TelLink clickSource="payment_options_cta">
                                        <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        Call 03 9022 4442
                                    </TelLink>
                                </Button>
                                <Button variant="outline" size="xl" asChild>
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <Calendar className="w-5 h-5 mr-2" />
                                        Book Online Today
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="py-12 bg-neutral-50 border-t border-neutral-200">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="rounded-2xl border border-neutral-200 bg-white p-6 md:p-8 shadow-soft">
                                <div className="flex items-start gap-3 mb-3">
                                    <Info className="h-5 w-5 text-neutral-500 flex-shrink-0 mt-1" />
                                    <h2 className="text-lg md:text-xl font-semibold text-neutral-800">
                                        Disclaimer
                                    </h2>
                                </div>
                                <p className="text-sm text-neutral-700 leading-relaxed">
                                    The information on this page is provided for general education only. Y3 Smiles Dental does not recommend, endorse, or promote any specific payment option, financial product, scheme, lender, insurer or health fund. Eligibility, approval, fees, interest rates, repayment terms and rebate amounts for each option are determined entirely by the relevant provider — including private health funds, Medicare, Afterpay, Humm and the Australian Taxation Office — not by the clinic. Examples of typical costs are indicative only and are not a quote. Before using any payment option, please read the provider's own terms and conditions, confirm your individual eligibility directly with them, and where relevant seek independent financial advice. Nothing on this page constitutes financial, legal, tax or medical advice.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default PaymentOptionsPage;